import { createDemoLearner } from './helpers'
import { SeededRng } from '../../../utils/deterministicRng'
import { AppState } from '../../../app/types'
import { LearnerAccount } from '../../types'

export function createAllLearners(rng: SeededRng): {
  djayden: { account: LearnerAccount; data: AppState }
  emma: { account: LearnerAccount; data: AppState }
  noah: { account: LearnerAccount; data: AppState }
  sophie: { account: LearnerAccount; data: AppState }
  liam: { account: LearnerAccount; data: AppState }
  noor: { account: LearnerAccount; data: AppState }
  yara: { account: LearnerAccount; data: AppState }
  sem: { account: LearnerAccount; data: AppState }
} {
  // Djayden — Power user, level 8 (5850 XP) — 150 XP from Level 9 (Pro→Master!)
  // Total questions ~93 — just under 100 so after one 10-question session
  // he unlocks "Honderdtal" achievement + DJ Pixel mascot (testable!)
  const djayden = createDemoLearner(rng, 'Djayden', 8, 5850, 7, {
    streak: {
      currentTotalStreak: 7,
      longestTotalStreak: 18,
      lastStreakQuestionId: 'TIJD-ANALOOG_demo_q44',
    },
    dailyStreak: {
      dailyGoalTarget: 10,
      todayQuestionsAnswered: 8,
      todayGoalReached: false,
      currentWeekDays: 3,
      consecutiveFullWeeks: 2,
      longestWeekStreak: 4,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      weeklyHistory: {
        '2026-W10': 5,
        '2026-W09': 5,
        '2026-W08': 4,
        '2026-W07': 5,
        '2026-W06': 4,
        '2026-W05': 5,
        '2026-W04': 3,
      },
      totalDailyGoalsReached: 31,
      totalPerfectSessions: 3,
    },
    // ~93 total questions (under 100 threshold for Honderdtal/DJ Pixel)
    completedQuestions: {
      Tijd: 12,
      Geld: 10,
      Getalbegrip: 18,
      Basisrekenen: 14,
      Metriek: 15,
      SpellingWW: 10,
      SpellingRegel: 6,
      Taalbegrip: 5,
      Studievaardig: 3,
      Topo: 0,
    },
    // Pre-populated achievements matching current stats (prevents popup overload)
    // Missing: 'century' (needs 100 questions — he's at 93, will unlock after 1 session!)
    unlockedAchievements: [
      'first-steps', // 5 questions ✓
      'getting-started', // 50 questions ✓
      'three-days', // 3 weekdays ✓
      'week-champion', // 5 weekdays (from weeklyHistory) ✓
      'hot-streak', // 10 correct streak (longestTotalStreak: 18) ✓
      'unstoppable', // 25 correct streak — no (18 < 25)
      'level-5', // level 5 ✓ (level 8)
      'level-10', // level 10 — no (level 8)
      'goal-getter', // 10 daily goals ✓ (31)
      'consistent', // 50 daily goals — no (31)
    ].filter((id) => !['unstoppable', 'level-10', 'consistent'].includes(id)),
    masteryScores: {
      'TIJD-ANALOOG': 97,
      'TIJD-DIGITAAL': 95,
      'GELD-MAAK': 92,
      'GELD-TERUG': 96,
      'GELD-WISSEL': 88,
      'TIJD-DUUR': 93,
      'GELD-BUDGET': 85,
      'TIJD-SCHEMA': 90,
      'GELD-KORTING': 82,
      'GET-BREUK-BASIS': 96,
      'GET-BREUK-LEZEN': 94,
      'GET-GROOT-POS': 97,
      'GET-GROOT-LEZEN': 92,
      'GET-GROOT-VERGELIJK': 95,
      'GET-KOMMA-WAARDE': 88,
      'GET-PROC-BASIS': 85,
      'GET-VERGELIJK': 90,
      'MET-LEN-OMREKEN': 96,
      'MET-LEN-METEN': 95,
      'MET-GEW-OMREKEN': 93,
      'MET-OPP-RECHT': 97,
      'MET-OMTREK-RECHT': 94,
      'MET-INH-BLOK': 88,
      'MET-HOEK-SOORTEN': 91,
      'MET-SYMMETRIE': 85,
      'SWW-TT-STAM': 82,
      'SWW-TT-INV-JIJ': 78,
      'SWW-VT-ZWAK-KOF': 75,
      'SWW-VD-KOF': 70,
      'SWW-TT-DSTAM': 68,
      'SPR-KLANK-JAGER': 75,
      'SPR-KLANK-BAKKER': 72,
      'SPR-KLANK-AA-EE-OO': 68,
      'SPR-HOOFDLETTER': 80,
      'TAA-ONT-BASIS': 65,
      'TAA-LEZEN-KERN': 60,
      'TAA-SOORT-LID': 55,
      'REK-TAFELS-BASIS': 95,
      'REK-TAFELS-TOEPASSEN': 88,
      'REK-CIJF-PLUS': 92,
      'REK-CIJF-MIN': 85,
      'REK-CIJF-KEER': 80,
      'REK-HANDIG-PLUS-MIN': 78,
      'REK-CONTEXT-PLUS-MIN': 75,
      'REK-SCHATTEN': 82,
      'REK-VOLGORDE': 70,
      'STU-TABEL': 50,
      'STU-GRAFIEK': 45,
      'TOPO-NL-PROV': 40,
      'TOPO-NL-WATER': 35,
    },
  })

  // Emma — Taal-specialist
  const emma = createDemoLearner(rng, 'Emma', 5, 2100, 7, {
    streak: {
      currentTotalStreak: 3,
      longestTotalStreak: 9,
      lastStreakQuestionId: 'SWW-TT-STAM_demo_q39',
    },
    dailyStreak: {
      dailyGoalTarget: 10,
      todayQuestionsAnswered: 5,
      todayGoalReached: false,
      currentWeekDays: 4,
      consecutiveFullWeeks: 1,
      longestWeekStreak: 2,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      weeklyHistory: {
        '2026-W10': 4,
        '2026-W09': 5,
        '2026-W08': 3,
        '2026-W07': 4,
        '2026-W06': 5,
      },
      totalDailyGoalsReached: 21,
      totalPerfectSessions: 1,
    },
    completedQuestions: {
      SpellingWW: 40,
      SpellingRegel: 35,
      Taalbegrip: 30,
      Metriek: 15,
      Getalbegrip: 12,
      Basisrekenen: 12,
      Tijd: 5,
      Geld: 5,
      Studievaardig: 8,
      Topo: 5,
    },
    // 167 total questions, level 5, streak 9, 21 daily goals
    unlockedAchievements: [
      'first-steps',
      'getting-started',
      'century',
      'three-days',
      'level-5',
      'goal-getter',
    ],
    masteryScores: {
      'SWW-TT-STAM': 92,
      'SWW-TT-INV-JIJ': 88,
      'SWW-VT-ZWAK-KOF': 85,
      'SWW-VD-KOF': 82,
      'SWW-TT-DSTAM': 80,
      'SWW-TT-MEERVOUD': 78,
      'SWW-VT-STERK': 75,
      'SWW-VD-HOMOFOON': 70,
      'SPR-KLANK-JAGER': 90,
      'SPR-KLANK-BAKKER': 87,
      'SPR-KLANK-AA-EE-OO': 85,
      'SPR-HOOFDLETTER': 92,
      'SPR-WEET-EI-IJ': 80,
      'SPR-WEET-D-T': 78,
      'SPR-LEEN-C-S-K': 72,
      'TAA-ONT-BASIS': 85,
      'TAA-LEZEN-KERN': 82,
      'TAA-LEZEN-VERW': 78,
      'TAA-SOORT-LID': 80,
      'TAA-SOORT-WW': 75,
      'TAA-SIGNAALWOORD': 70,
      'TAA-TEKSTSOORT': 65,
      'MET-LEN-OMREKEN': 55,
      'MET-LEN-METEN': 50,
      'MET-GEW-OMREKEN': 45,
      'GET-GROOT-POS': 50,
      'GET-GROOT-LEZEN': 48,
      'GET-BREUK-BASIS': 42,
      'GET-BREUK-LEZEN': 35,
      'GET-KOMMA-WAARDE': 38,
      'REK-TAFELS-BASIS': 55,
      'REK-TAFELS-TOEPASSEN': 40,
      'REK-CIJF-PLUS': 38,
      'REK-CIJF-MIN': 30,
      'REK-CONTEXT-PLUS-MIN': 32,
      'REK-SCHATTEN': 45,
      'TIJD-ANALOOG': 55,
      'GELD-MAAK': 50,
      'GELD-TERUG': 42,
      'STU-ALFABET': 60,
      'STU-WOORDENBOEK': 55,
      'TOPO-NL-PROV': 35,
    },
  })

  // Noah — Balanced learner
  const noah = createDemoLearner(rng, 'Noah', 6, 3050, 7, {
    streak: {
      currentTotalStreak: 0,
      longestTotalStreak: 12,
      lastStreakQuestionId: 'GET-BREUK-BASIS_demo_q29',
    },
    dailyStreak: {
      dailyGoalTarget: 10,
      todayQuestionsAnswered: 10,
      todayGoalReached: true,
      currentWeekDays: 4,
      consecutiveFullWeeks: 1,
      longestWeekStreak: 3,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      weeklyHistory: {
        '2026-W10': 5,
        '2026-W09': 3,
        '2026-W08': 5,
        '2026-W07': 4,
        '2026-W06': 5,
        '2026-W05': 3,
      },
      totalDailyGoalsReached: 25,
      totalPerfectSessions: 2,
    },
    // 175 total questions, level 6, streak 12, 25 daily goals
    unlockedAchievements: [
      'first-steps',
      'getting-started',
      'century',
      'three-days',
      'hot-streak',
      'level-5',
      'goal-getter',
    ],
    completedQuestions: {
      Getalbegrip: 30,
      Basisrekenen: 20,
      Tijd: 13,
      Geld: 12,
      Metriek: 25,
      SpellingWW: 20,
      SpellingRegel: 18,
      Taalbegrip: 15,
      Studievaardig: 12,
      Topo: 10,
    },
    masteryScores: {
      'GET-BREUK-BASIS': 78,
      'GET-GROOT-POS': 82,
      'GET-GROOT-LEZEN': 75,
      'GET-KOMMA-WAARDE': 70,
      'GET-PROC-BASIS': 65,
      'GET-VERGELIJK': 72,
      'REK-TAFELS-BASIS': 80,
      'REK-TAFELS-TOEPASSEN': 72,
      'REK-CIJF-PLUS': 75,
      'REK-CIJF-MIN': 68,
      'REK-HANDIG-PLUS-MIN': 65,
      'REK-CONTEXT-PLUS-MIN': 62,
      'REK-SCHATTEN': 78,
      'REK-VOLGORDE': 60,
      'TIJD-ANALOOG': 80,
      'TIJD-DIGITAAL': 75,
      'GELD-MAAK': 72,
      'GELD-TERUG': 68,
      'TIJD-DUUR': 65,
      'MET-LEN-OMREKEN': 75,
      'MET-LEN-METEN': 72,
      'MET-GEW-OMREKEN': 68,
      'MET-OPP-RECHT': 70,
      'MET-OMTREK-RECHT': 65,
      'SWW-TT-STAM': 72,
      'SWW-TT-INV-JIJ': 68,
      'SWW-VT-ZWAK-KOF': 60,
      'SWW-VD-KOF': 55,
      'SPR-KLANK-JAGER': 70,
      'SPR-KLANK-BAKKER': 65,
      'SPR-HOOFDLETTER': 72,
      'TAA-ONT-BASIS': 68,
      'TAA-LEZEN-KERN': 65,
      'TAA-SOORT-LID': 60,
      'STU-TABEL': 58,
      'STU-GRAFIEK': 55,
      'STU-KAART': 50,
      'TOPO-NL-PROV': 60,
      'TOPO-NL-WATER': 55,
      'TOPO-NL-STEDEN': 50,
    },
  })

  // Sophie — Top student
  const sophie = createDemoLearner(rng, 'Sophie', 9, 7800, 8, {
    streak: {
      currentTotalStreak: 12,
      longestTotalStreak: 32,
      lastStreakQuestionId: 'SWW-TT-STAM_demo_q49',
    },
    dailyStreak: {
      dailyGoalTarget: 10,
      todayQuestionsAnswered: 12,
      todayGoalReached: true,
      currentWeekDays: 4,
      consecutiveFullWeeks: 9,
      longestWeekStreak: 9,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      weeklyHistory: {
        '2026-W10': 5,
        '2026-W09': 5,
        '2026-W08': 5,
        '2026-W07': 5,
        '2026-W06': 5,
        '2026-W05': 5,
        '2026-W04': 5,
        '2026-W03': 5,
        '2026-W02': 5,
      },
      totalDailyGoalsReached: 45,
      totalPerfectSessions: 4,
    },
    // 318 total, level 9, streak 32, 9 consecutive weeks, 45 daily goals
    unlockedAchievements: [
      'first-steps',
      'getting-started',
      'century',
      'three-days',
      'week-champion',
      'fire-master',
      'hot-streak',
      'unstoppable',
      'legendary-streak',
      'level-5',
      'level-10',
      'goal-getter',
      'consistent',
    ],
    completedQuestions: {
      SpellingWW: 50,
      SpellingRegel: 45,
      Taalbegrip: 40,
      Getalbegrip: 38,
      Basisrekenen: 35,
      Tijd: 18,
      Geld: 17,
      Metriek: 30,
      Studievaardig: 25,
      Topo: 20,
    },
    masteryScores: {
      'SWW-TT-STAM': 98,
      'SWW-TT-INV-JIJ': 96,
      'SWW-VT-ZWAK-KOF': 95,
      'SWW-VD-KOF': 97,
      'SWW-TT-DSTAM': 94,
      'SWW-TT-MEERVOUD': 96,
      'SWW-VT-STERK': 92,
      'SWW-VD-HOMOFOON': 90,
      'SWW-TT-GEBIEDENDE': 88,
      'SPR-KLANK-JAGER': 97,
      'SPR-KLANK-BAKKER': 96,
      'SPR-KLANK-AA-EE-OO': 95,
      'SPR-HOOFDLETTER': 98,
      'SPR-WEET-EI-IJ': 94,
      'SPR-WEET-D-T': 96,
      'SPR-LEEN-C-S-K': 92,
      'SPR-TREMA-MV': 88,
      'TAA-ONT-BASIS': 97,
      'TAA-LEZEN-KERN': 96,
      'TAA-LEZEN-VERW': 95,
      'TAA-SOORT-LID': 98,
      'TAA-SOORT-WW': 94,
      'TAA-SIGNAALWOORD': 96,
      'TAA-TEKSTSOORT': 92,
      'TAA-WOORD-SYNONIEM': 90,
      'TAA-ONT-GEZEGDE': 88,
      'GET-BREUK-BASIS': 97,
      'GET-BREUK-LEZEN': 95,
      'GET-GROOT-POS': 98,
      'GET-GROOT-LEZEN': 96,
      'GET-GROOT-VERGELIJK': 95,
      'GET-KOMMA-WAARDE': 93,
      'GET-PROC-BASIS': 92,
      'GET-VERGELIJK': 94,
      'REK-TAFELS-BASIS': 98,
      'REK-TAFELS-TOEPASSEN': 95,
      'REK-TAFELS-UITBREIDEN': 90,
      'REK-CIJF-PLUS': 96,
      'REK-CIJF-MIN': 94,
      'REK-CIJF-KEER': 92,
      'REK-HANDIG-PLUS-MIN': 90,
      'REK-HANDIG-KEER': 85,
      'REK-CONTEXT-PLUS-MIN': 92,
      'REK-CONTEXT-KEER-DEEL': 88,
      'REK-SCHATTEN': 94,
      'REK-VOLGORDE': 90,
      'REK-GEMENGD-BASIS': 85,
      'TIJD-ANALOOG': 92,
      'TIJD-DIGITAAL': 90,
      'GELD-MAAK': 88,
      'GELD-TERUG': 92,
      'GELD-WISSEL': 85,
      'TIJD-DUUR': 88,
      'GELD-BUDGET': 82,
      'GELD-KORTING': 80,
      'MET-LEN-OMREKEN': 90,
      'MET-LEN-METEN': 88,
      'MET-GEW-OMREKEN': 85,
      'MET-OPP-RECHT': 92,
      'MET-OMTREK-RECHT': 88,
      'MET-INH-BLOK': 82,
      'MET-HOEK-SOORTEN': 80,
      'STU-TABEL': 82,
      'STU-GRAFIEK': 78,
      'STU-KAART': 75,
      'STU-ALFABET': 85,
      'STU-WOORDENBOEK': 80,
      'STU-DIAGRAM': 72,
      'TOPO-NL-PROV': 82,
      'TOPO-NL-WATER': 78,
      'TOPO-NL-STEDEN': 75,
      'TOPO-EU-LAND': 70,
      'TOPO-EU-HOOFDST': 65,
    },
  })

  // Liam — Worstelt, heeft extra aandacht nodig
  const liam = createDemoLearner(rng, 'Liam', 3, 600, 8, {
    streak: {
      currentTotalStreak: 1,
      longestTotalStreak: 4,
      lastStreakQuestionId: 'REK-TAFELS-BASIS_demo_q12',
    },
    dailyStreak: {
      dailyGoalTarget: 5,
      todayQuestionsAnswered: 3,
      todayGoalReached: false,
      currentWeekDays: 2,
      consecutiveFullWeeks: 0,
      longestWeekStreak: 1,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      weeklyHistory: {
        '2026-W10': 3,
        '2026-W09': 2,
        '2026-W08': 1,
        '2026-W07': 3,
        '2026-W06': 2,
      },
      totalDailyGoalsReached: 11,
      totalPerfectSessions: 0,
    },
    // 87 total, level 3, streak 4, 11 daily goals
    unlockedAchievements: ['first-steps', 'getting-started', 'goal-getter'],
    completedQuestions: {
      Basisrekenen: 18,
      Getalbegrip: 15,
      Tijd: 10,
      Geld: 8,
      Metriek: 12,
      SpellingWW: 10,
      SpellingRegel: 5,
      Taalbegrip: 4,
      Studievaardig: 2,
      Topo: 3,
    },
    masteryScores: {
      'REK-TAFELS-BASIS': 58,
      'REK-TAFELS-TOEPASSEN': 42,
      'REK-CIJF-PLUS': 45,
      'REK-CIJF-MIN': 30,
      'REK-CONTEXT-PLUS-MIN': 28,
      'GET-GROOT-POS': 50,
      'GET-GROOT-LEZEN': 48,
      'GET-BREUK-BASIS': 25,
      'GET-KOMMA-WAARDE': 35,
      'TIJD-ANALOOG': 50,
      'TIJD-DIGITAAL': 45,
      'GELD-MAAK': 42,
      'GELD-TERUG': 35,
      'MET-LEN-METEN': 48,
      'MET-LEN-OMREKEN': 35,
      'MET-GEW-OMREKEN': 25,
      'MET-OPP-RECHT': 20,
      'SWW-TT-STAM': 30,
      'SWW-TT-INV-JIJ': 22,
      'SWW-VT-ZWAK-KOF': 18,
      'SWW-VD-KOF': 15,
      'SPR-KLANK-JAGER': 38,
      'SPR-HOOFDLETTER': 45,
      'TAA-ONT-BASIS': 28,
      'TAA-LEZEN-KERN': 22,
      'TOPO-NL-PROV': 30,
    },
  })

  // Noor — All-rounder
  const noor = createDemoLearner(rng, 'Noor', 8, 5900, 8, {
    streak: {
      currentTotalStreak: 5,
      longestTotalStreak: 23,
      lastStreakQuestionId: 'GET-GROOT-POS_demo_q34',
    },
    dailyStreak: {
      dailyGoalTarget: 10,
      todayQuestionsAnswered: 7,
      todayGoalReached: false,
      currentWeekDays: 3,
      consecutiveFullWeeks: 2,
      longestWeekStreak: 4,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      weeklyHistory: {
        '2026-W10': 4,
        '2026-W09': 5,
        '2026-W08': 5,
        '2026-W07': 3,
        '2026-W06': 5,
        '2026-W05': 4,
        '2026-W04': 5,
      },
      totalDailyGoalsReached: 31,
      totalPerfectSessions: 2,
    },
    // 218 total, level 8, streak 23, 31 daily goals
    unlockedAchievements: [
      'first-steps',
      'getting-started',
      'century',
      'three-days',
      'hot-streak',
      'level-5',
      'goal-getter',
    ],
    completedQuestions: {
      Getalbegrip: 35,
      Basisrekenen: 25,
      Metriek: 30,
      Tijd: 15,
      Geld: 13,
      SpellingWW: 25,
      SpellingRegel: 22,
      Taalbegrip: 20,
      Studievaardig: 18,
      Topo: 15,
    },
    masteryScores: {
      'GET-BREUK-BASIS': 90,
      'GET-BREUK-LEZEN': 88,
      'GET-GROOT-POS': 92,
      'GET-GROOT-LEZEN': 85,
      'GET-GROOT-VERGELIJK': 88,
      'GET-KOMMA-WAARDE': 82,
      'GET-PROC-BASIS': 78,
      'GET-VERGELIJK': 85,
      'GET-KOMMA-PLUS': 75,
      'REK-TAFELS-BASIS': 92,
      'REK-TAFELS-TOEPASSEN': 85,
      'REK-CIJF-PLUS': 88,
      'REK-CIJF-MIN': 82,
      'REK-CIJF-KEER': 78,
      'REK-HANDIG-PLUS-MIN': 75,
      'REK-CONTEXT-PLUS-MIN': 80,
      'REK-SCHATTEN': 88,
      'REK-VOLGORDE': 82,
      'MET-LEN-OMREKEN': 92,
      'MET-LEN-METEN': 90,
      'MET-GEW-OMREKEN': 88,
      'MET-OPP-RECHT': 85,
      'MET-OMTREK-RECHT': 82,
      'MET-INH-BLOK': 78,
      'MET-HOEK-SOORTEN': 80,
      'MET-SYMMETRIE': 75,
      'MET-COORDINATEN': 72,
      'TIJD-ANALOOG': 85,
      'TIJD-DIGITAAL': 82,
      'GELD-MAAK': 80,
      'GELD-TERUG': 78,
      'GELD-WISSEL': 72,
      'TIJD-DUUR': 75,
      'GELD-BUDGET': 68,
      'SWW-TT-STAM': 78,
      'SWW-TT-INV-JIJ': 75,
      'SWW-VT-ZWAK-KOF': 70,
      'SWW-VD-KOF': 68,
      'SWW-TT-DSTAM': 65,
      'SWW-VT-STERK': 60,
      'SPR-KLANK-JAGER': 75,
      'SPR-KLANK-BAKKER': 72,
      'SPR-KLANK-AA-EE-OO': 70,
      'SPR-HOOFDLETTER': 78,
      'SPR-WEET-EI-IJ': 65,
      'SPR-WEET-D-T': 62,
      'TAA-ONT-BASIS': 72,
      'TAA-LEZEN-KERN': 70,
      'TAA-LEZEN-VERW': 65,
      'TAA-SOORT-LID': 68,
      'TAA-SOORT-WW': 62,
      'STU-TABEL': 65,
      'STU-GRAFIEK': 62,
      'STU-KAART': 58,
      'STU-ALFABET': 70,
      'STU-CIRKEL': 55,
      'TOPO-NL-PROV': 68,
      'TOPO-NL-WATER': 62,
      'TOPO-NL-STEDEN': 58,
      'TOPO-EU-LAND': 50,
    },
  })

  // Yara — Math focus
  const yara = createDemoLearner(rng, 'Yara', 4, 1350, 6, {
    streak: {
      currentTotalStreak: 4,
      longestTotalStreak: 8,
      lastStreakQuestionId: 'MET-LEN-OMREKEN_demo_q29',
    },
    dailyStreak: {
      dailyGoalTarget: 10,
      todayQuestionsAnswered: 6,
      todayGoalReached: false,
      currentWeekDays: 2,
      consecutiveFullWeeks: 0,
      longestWeekStreak: 1,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      weeklyHistory: {
        '2026-W10': 3,
        '2026-W09': 5,
        '2026-W08': 4,
        '2026-W07': 2,
        '2026-W06': 3,
      },
      totalDailyGoalsReached: 17,
      totalPerfectSessions: 1,
    },
    // 108 total, level 4, streak 8, 17 daily goals
    unlockedAchievements: [
      'first-steps',
      'getting-started',
      'century',
      'three-days',
      'goal-getter',
    ],
    completedQuestions: {
      Metriek: 30,
      Tijd: 13,
      Geld: 12,
      Getalbegrip: 22,
      Basisrekenen: 15,
      SpellingWW: 8,
      SpellingRegel: 5,
      Taalbegrip: 3,
      Studievaardig: 0,
      Topo: 0,
    },
    masteryScores: {
      'MET-LEN-OMREKEN': 82,
      'MET-LEN-METEN': 80,
      'MET-GEW-OMREKEN': 75,
      'MET-OPP-RECHT': 78,
      'MET-OMTREK-RECHT': 72,
      'MET-INH-LITER': 68,
      'MET-HOEK-SOORTEN': 65,
      'TIJD-ANALOOG': 78,
      'TIJD-DIGITAAL': 75,
      'GELD-MAAK': 72,
      'GELD-TERUG': 68,
      'GELD-TELLEN': 70,
      'TIJD-DUUR': 60,
      'GET-GROOT-POS': 70,
      'GET-GROOT-LEZEN': 65,
      'GET-BREUK-BASIS': 60,
      'GET-VERGELIJK': 58,
      'REK-TAFELS-BASIS': 75,
      'REK-TAFELS-TOEPASSEN': 65,
      'REK-CIJF-PLUS': 60,
      'REK-CIJF-MIN': 52,
      'REK-CONTEXT-PLUS-MIN': 55,
      'REK-SCHATTEN': 68,
      'SWW-TT-STAM': 45,
      'SWW-TT-INV-JIJ': 40,
      'SWW-VT-ZWAK-KOF': 28,
      'SWW-VD-KOF': 22,
      'SPR-KLANK-JAGER': 42,
      'SPR-KLANK-BAKKER': 35,
      'SPR-HOOFDLETTER': 50,
      'SPR-KLANK-AA-EE-OO': 30,
      'TAA-ONT-BASIS': 30,
      'TAA-LEZEN-KERN': 25,
    },
  })

  // Sem — Topo & Studievaardig focus
  const sem = createDemoLearner(rng, 'Sem', 3, 750, 6, {
    streak: {
      currentTotalStreak: 2,
      longestTotalStreak: 5,
      lastStreakQuestionId: 'TOPO-NL-PROV_demo_q24',
    },
    dailyStreak: {
      dailyGoalTarget: 5,
      todayQuestionsAnswered: 3,
      todayGoalReached: false,
      currentWeekDays: 1,
      consecutiveFullWeeks: 0,
      longestWeekStreak: 1,
      lastActiveDate: new Date().toISOString().slice(0, 10),
      weeklyHistory: {
        '2026-W10': 2,
        '2026-W09': 3,
        '2026-W08': 1,
        '2026-W07': 2,
        '2026-W06': 3,
      },
      totalDailyGoalsReached: 11,
      totalPerfectSessions: 0,
    },
    // 103 total, level 3, streak 5, 11 daily goals
    unlockedAchievements: [
      'first-steps',
      'getting-started',
      'century',
      'goal-getter',
    ],
    completedQuestions: {
      Topo: 28,
      Studievaardig: 22,
      Tijd: 8,
      Geld: 7,
      Metriek: 12,
      Getalbegrip: 8,
      Basisrekenen: 8,
      SpellingWW: 5,
      SpellingRegel: 3,
      Taalbegrip: 2,
    },
    masteryScores: {
      'TOPO-NL-PROV': 85,
      'TOPO-NL-WATER': 82,
      'TOPO-NL-STEDEN': 78,
      'TOPO-NL-BUURLANDEN': 80,
      'TOPO-NL-LANDSCHAP': 72,
      'TOPO-EU-LAND': 65,
      'TOPO-WERELD-CONT': 60,
      'STU-KAART': 78,
      'STU-TABEL': 72,
      'STU-GRAFIEK': 68,
      'STU-ALFABET': 75,
      'STU-WOORDENBOEK': 65,
      'STU-INHOUDSOPGAVE': 60,
      'TIJD-ANALOOG': 55,
      'GELD-MAAK': 50,
      'GELD-TELLEN': 48,
      'MET-LEN-METEN': 45,
      'MET-LEN-OMREKEN': 40,
      'GET-GROOT-POS': 42,
      'GET-GROOT-LEZEN': 38,
      'GET-BREUK-BASIS': 25,
      'REK-TAFELS-BASIS': 48,
      'REK-TAFELS-TOEPASSEN': 32,
      'REK-CIJF-PLUS': 28,
      'REK-CIJF-MIN': 18,
      'SWW-TT-STAM': 35,
      'SWW-TT-INV-JIJ': 30,
      'SPR-HOOFDLETTER': 40,
      'TAA-ONT-BASIS': 20,
    },
  })

  return { djayden, emma, noah, sophie, liam, noor, yara, sem }
}
